import { useQuery } from '@apollo/client';
import { getDataFromTree } from '@apollo/client/react/ssr';
import { NextPage } from 'next';
import { ApolloPageContext, PageProps } from 'helpers/next-page';
// Api
import { GET_STREAMS } from 'api/streams/queries';
// Types
import { GetStreams, GetStreamsVariables } from 'api/streams/types/GetStreams';
import {
  StreamOrderBy,
  StreamStatus,
  SortDirection,
} from 'api/graphql-global-types';
// Helpers
import { getAthleteTabMetaTitle } from 'helpers/athleteTabSeo';
import { getAthleteTabPageProps } from 'helpers/athleteTabPageGuard';
import { computeOrderStreamPath, getEnvLink } from 'helpers/routes';
import { generateItemListStructuredData } from 'helpers/structured-data';
// Hocks
import withApollo from 'hocs/withApollo';
// Hooks
import {
  useRedirectOnError,
  useRedirectIfNoContent,
  useAthletePageData,
} from 'hooks';
// Layouts
import { getProfileAvatarPreload } from 'helpers/profileAvatar';
import Head from 'layouts/Head/Head';
import Header from 'layouts/Header/Header';
import Main from 'layouts/Main/Main';
import Footer from 'layouts/Footer/Footer';
// Context
import { FiltersProvider } from 'components/common3/PageWithFiltersWrapper/PageWithFiltersContext';
// Components
import AppBannerAndModal from 'components/common3/AppBannerAndModal/AppBannerAndModal';
import Description from 'components/Athlete/Description/Description';
import StreamsContainer from 'components/Streams/Streams';
import NotFoundPage from 'components/NotFoundPage/NotFoundPage';

const StreamsContent = (): JSX.Element | null => {
  const {
    error,
    skip,
    cleanedSlug,
    athleteSlug,
    metaData,
    store,
  } = useAthletePageData();

  const { data: streamsData } = useQuery<GetStreams, GetStreamsVariables>(
    GET_STREAMS,
    {
      variables: {
        input: {
          storeId: store?.id || '',
          streamStatus: [StreamStatus.Active, StreamStatus.Scheduled],
        },
      },
      skip: !store,
    }
  );

  const streams = streamsData?.streamsV2?.entities;

  useRedirectOnError({ error, redirectTo: `/${athleteSlug}` });

  // Redirect to profile home if streams content is not available
  useRedirectIfNoContent({
    hasContent: (store?.hasStreams || store?.hasActiveStreams) ?? false,
    redirectTo: `/${athleteSlug}`,
    skip: !store || skip,
  });

  if (!store) {
    return null;
  }

  const canonicalLink = `${getEnvLink()}/${cleanedSlug}/streams`;

  const streamItems =
    streams?.map((item) => ({
      url: `${getEnvLink()}${computeOrderStreamPath(item)}`,
    })) || [];

  const structuredData = generateItemListStructuredData(
    `${metaData.name} Streams`,
    streamItems
  );

  return (
    <>
      <Head
        title={getAthleteTabMetaTitle(metaData.name, 'streams')}
        description={`Watch ${metaData.name}'s live streams and replays on MILLIONS.`}
        image={metaData.avatarUrl}
        imagePreload={getProfileAvatarPreload(metaData.avatarUrl)}
        canonicalLink={canonicalLink}
        robots={store.hasStreams ? 'index, follow' : 'noindex, follow'}
        structuredData={structuredData}
      />
      <AppBannerAndModal
        when
        variant="profile"
        username={cleanedSlug ?? undefined}
        profileImageUrl={metaData.avatarUrl ?? undefined}
      />
      <Header />

      <div className="streamsPageRoot">
        <Main backgroundColor="background-dim" withoutSidesPadding>
          <Description store={store} />
          {(store.hasStreams || store.hasActiveStreams) && (
            <FiltersProvider
              storesListFilter="hasActiveStreams"
              searchKey="searchTerm"
              hideContentCreatorsFilter
              hideAthletesFilter
              hideOrganizationsFilter
              hasAthleteSlug
              initialSorting={{
                direction: SortDirection.ASC,
                order: StreamOrderBy.ScheduleDate,
              }}
            >
              <StreamsContainer
                isPage={false}
                storeSlug={athleteSlug as string}
              />
            </FiltersProvider>
          )}
        </Main>
      </div>

      <Footer />
    </>
  );
};

const Streams: NextPage<PageProps> = ({ isNotFound }) =>
  isNotFound ? <NotFoundPage /> : <StreamsContent />;

Streams.getInitialProps = async (ctx: ApolloPageContext): Promise<PageProps> =>
  getAthleteTabPageProps(ctx, 'streams');

export default withApollo(Streams, { getDataFromTree });
